// src/components/manuscripts/ManuscriptsWorkspace.jsx
//
// Library -> Manuscripts, the middle section. Built to the prototype attached
// to the ticket; the sidebar and the top toolbar are out of scope and untouched.
//
// Two views live in here, as they do in the prototype: the gallery of sections
// with the calendar rail, and the detail view a card opens into. Clicking a
// card used to go straight to /editor/:id -- that is now the "Continue Writing"
// button one level in.
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Menu, MenuItem, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { postJSON } from '../../utils/api';
import ManuscriptCard from './ManuscriptCard';
import ManuscriptSection from './ManuscriptSection';
import { QUIET_BUTTON } from './manuscriptStyles';
import ManuscriptCalendar from './ManuscriptCalendar';
import ManuscriptDetail from './ManuscriptDetail';
import '../../styles/ManuscriptWorkspace.css';

// Word count is deliberately absent from this menu while `wordCount` is not a
// field anyone writes -- see `wordCountLabel`. Offering a sort that silently
// returns the list in the order it arrived is worse than not offering it.
// `sortManuscripts` still implements the key, and the day the field lands this
// is one line.
const SORTS = [
  { key: 'lastEdited', label: 'Last edited' },
  { key: 'title', label: 'Title' },
];

export function sortManuscripts(list, key) {
  const copy = [...list];
  if (key === 'title') {
    return copy.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }
  if (key === 'wordCount') {
    return copy.sort((a, b) => (b.wordCount ?? 0) - (a.wordCount ?? 0));
  }
  return copy.sort((a, b) => new Date(b.lastEdited || 0) - new Date(a.lastEdited || 0));
}

export default function ManuscriptsWorkspace({
  manuscripts,
  currentUserId,
  search,
  onSearchChange,
  onOpenEditor,
  onOpenStructure,
  onOpenPricing,
  onDeleteDraft,
  onLinkCover,
  onUnlinkCover,
  onManuscriptRemoved,
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [sort, setSort] = useState('lastEdited');
  const [sortAnchor, setSortAnchor] = useState(null);

  // The invitation that puts you on a collaboration manuscript used to be
  // prefetched here into a map keyed by draft, and handed down. A live check
  // found Unassign never rendering even with a matching accepted invitation on
  // the account, and the map is why that was hard to see: the lookup, the
  // fetch and the button were in three different places, and every failure
  // mode -- fetch rejected, shape unexpected, key mismatch, state not yet
  // settled on first paint -- arrived at the same silent `undefined`.
  //
  // The detail view now resolves its own invitation when it opens, which is
  // also the only moment one is needed. One request per collaboration
  // manuscript opened, instead of one per visit to the tab.

  const matches = (m) =>
    !search || (m.title || '').toLowerCase().includes(search.toLowerCase());

  const sections = useMemo(() => {
    const visible = manuscripts.filter(matches);
    const mine = visible.filter((m) => String(m.author_id) === String(currentUserId));
    return {
      revision: sortManuscripts(visible.filter((m) => m.reviewNotes), sort),
      inProgress: sortManuscripts(mine.filter((m) => !m.reviewNotes), sort),
      collaboration: sortManuscripts(
        visible.filter((m) => String(m.author_id) !== String(currentUserId) && !m.reviewNotes),
        sort
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manuscripts, search, sort, currentUserId]);

  const selected = useMemo(
    () => manuscripts.find((m) => m._id === selectedId) || null,
    [manuscripts, selectedId]
  );

  // A card deleted while its detail view is open has nowhere to go back to.
  useEffect(() => {
    if (selectedId && !selected) setSelectedId(null);
  }, [selectedId, selected]);

  const isCollaboration = (m) => String(m.author_id) !== String(currentUserId);

  /**
   * The owner-only actions, or nothing. Pricing is one: the split it saves is the
   * author's, and the server refuses anyone else's Save.
   *
   * `onDelete={collab ? undefined : () => onDeleteDraft?.(m)}` reads like it
   * passes nothing when there is nothing to pass, and does not: the arrow is
   * truthy whether or not `onDeleteDraft` exists, so the card's overflow menu
   * rendered with items behind which nothing was wired. Spreading the whole
   * group keeps the keys absent instead of present-and-undefined.
   */
  const ownerActions = (collab, onDelete) => {
    if (collab) return {};
    return {
      ...(onDeleteDraft ? { onDelete } : {}),
      ...(onLinkCover ? { onLinkCover } : {}),
      ...(onUnlinkCover ? { onUnlinkCover } : {}),
      ...(onOpenPricing ? { onOpenPricing } : {}),
    };
  };

  const handleUnassign = async (invitation) => {
    try {
      await postJSON(`/api/invitations/${invitation.id}/unassign-self`, {});
      onManuscriptRemoved?.(invitation.draftId);
      setSelectedId(null);
    } catch {
      alert('Could not unassign you from this manuscript. Please try again.');
    }
  };

  if (selected) {
    const collab = isCollaboration(selected);
    return (
      <ManuscriptDetail
        manuscript={selected}
        isCollaboration={collab}
        onBack={() => setSelectedId(null)}
        onOpenEditor={onOpenEditor}
        onDelete={collab || !onDeleteDraft ? undefined : () => onDeleteDraft(selected)}
        onUnassign={handleUnassign}
        onOpenStructure={onOpenStructure}
        onOpenPricing={collab ? undefined : onOpenPricing}
        onLinkCover={collab ? undefined : onLinkCover}
      />
    );
  }

  const focus = sortManuscripts(
    manuscripts.filter((m) => String(m.author_id) === String(currentUserId)),
    'lastEdited'
  ).slice(0, 3);

  const section = (key, title, items, opts = {}) => (
    <ManuscriptSection key={key} id={key} title={title} items={items} {...opts}>
      {items.map((m) => {
        const collab = isCollaboration(m);
        return (
          <ManuscriptCard
            key={m._id}
            manuscript={m}
            isCollaboration={collab}
            onOpen={() => setSelectedId(m._id)}
            {...ownerActions(collab, () => onDeleteDraft?.(m))}
            onOpenStructure={onOpenStructure}
          />
        );
      })}
    </ManuscriptSection>
  );

  const nothingAtAll =
    !sections.revision.length && !sections.inProgress.length && !sections.collaboration.length;

  return (
    <>
      <div className="rv-manuscripts__hero">
        <div>
          <h1 className="rv-manuscripts__greeting">My Manuscripts</h1>
          <p className="rv-manuscripts__subtitle">
            Your ideas. Your stories. One place to bring them to life.
          </p>
        </div>

        <div className="rv-manuscripts__hero-controls">
          {/* The prototype puts search in the top toolbar, which the ticket
              told us to leave alone -- so the field this tab already had stays,
              moved onto the hero row beside the sort control. */}
          <TextField
            size="small"
            placeholder="Search manuscripts…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            inputProps={{ 'aria-label': 'Search manuscripts' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 260,
              // Pill, to match the sort control beside it and the prototype's
              // own search field.
              '& .MuiOutlinedInput-root': { borderRadius: 999 },
            }}
          />
          <Button
            size="small"
            variant="outlined"
            endIcon={<ExpandMoreIcon />}
            onClick={(e) => setSortAnchor(e.currentTarget)}
            sx={{ ...QUIET_BUTTON, borderRadius: 999, whiteSpace: 'nowrap' }}
          >
            Sort by: {SORTS.find((s) => s.key === sort).label}
          </Button>
          <Menu
            anchorEl={sortAnchor}
            open={Boolean(sortAnchor)}
            onClose={() => setSortAnchor(null)}
          >
            {SORTS.map((s) => (
              <MenuItem
                key={s.key}
                selected={s.key === sort}
                onClick={() => { setSort(s.key); setSortAnchor(null); }}
              >
                {s.label}
              </MenuItem>
            ))}
          </Menu>
        </div>
      </div>

      <div className="rv-manuscripts__workspace">
        <div className="rv-manuscripts__sections">
          {/* Three sections, not the prototype's two. "Needs Revision" is what
              the sidebar's MANUSCRIPTS badge counts and the only place a
              rejected draft surfaces, so folding it into In Progress would
              lose a signal the tab exists to carry. */}
          {section('revision', 'Needs Revision', sections.revision, {
            modifier: 'revision',
            showCount: true,
          })}
          {section('inProgress', 'In Progress', sections.inProgress)}
          {section('collaboration', 'Collaboration', sections.collaboration)}

          {nothingAtAll && (
            <p className="rv-manuscripts__empty">
              {search
                ? `No manuscripts match “${search}”.`
                : "You don't have any manuscripts yet. Start writing in the Editor!"}
            </p>
          )}
        </div>

        <ManuscriptCalendar focus={focus} onSelectManuscript={(m) => setSelectedId(m._id)} />
      </div>
    </>
  );
}
